import { useState, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { reportsApi } from '@/services/api';
import { useApiData } from '@/hooks/useApiData';

const reportTypes = [
  { id: 'all', label: 'All Reports', icon: 'ri-file-list-line' },
  { id: 'completed', label: 'Completed', icon: 'ri-check-double-line' },
  { id: 'pending', label: 'Pending', icon: 'ri-time-line' },
  { id: 'failed', label: 'Failed', icon: 'ri-error-warning-line' },
];

interface ReportRow {
  id: number;
  location: string;
  land_size_acres: number;
  current_use: string;
  requester_name: string;
  requester_email: string;
  monetization_score: number | null;
  recommended_use: string | null;
  estimated_rent: number | null;
  status: string;
  created_at: string;
  completed_at: string | null;
}

function statusBadge(s: string) {
  const map: Record<string, string> = {
    completed: 'bg-teal-500/15 text-teal-400', pending: 'bg-accent-100 text-accent-700',
    processing: 'bg-blue-500/15 text-blue-400', failed: 'bg-rose-500/15 text-rose-400',
  };
  return map[s] || 'bg-background-200/50 text-foreground-500';
}

function formatDate(d: string) {
  return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
}

function toDateOnly(str: string): string {
  if (!str) return '';
  return str.slice(0, 10);
}

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ReportRow | null>(null);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: apiResp, loading } = useApiData<{ data: ReportRow[]; pagination: { total: number } }>(
    () => reportsApi.list({ status: selectedReport !== 'all' ? selectedReport : '', per_page: 50, page: 1 }).then(r => r as unknown as { data: ReportRow[]; pagination: { total: number } }),
    [selectedReport, refreshKey]
  );

  const apiReports: ReportRow[] = apiResp?.data || [];

  const reports = useMemo(() => {
    let result = [...apiReports];
    if (dateFrom) {
      result = result.filter(r => {
        const d = toDateOnly(r.created_at);
        return d >= dateFrom;
      });
    }
    if (dateTo) {
      result = result.filter(r => {
        const d = toDateOnly(r.completed_at || r.created_at);
        return d <= dateTo;
      });
    }
    return result;
  }, [apiReports, dateFrom, dateTo]);

  const totalCount = apiResp?.pagination?.total || apiReports.length;

  const stats = [
    { label: 'Total Reports', value: totalCount.toString(), icon: 'ri-file-list-line', color: 'accent' },
    { label: 'Completed', value: apiReports.filter(r => r.status === 'completed').length.toString(), icon: 'ri-check-double-line', color: 'teal' },
    { label: 'Avg Score', value: apiReports.filter(r => r.monetization_score).length > 0
      ? Math.round(apiReports.filter(r => r.monetization_score).reduce((a, r) => a + (r.monetization_score || 0), 0) / apiReports.filter(r => r.monetization_score).length).toString()
      : '-', icon: 'ri-funds-line', color: 'secondary' },
  ];

  const hasDateFilter = dateFrom || dateTo;
  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await reportsApi.delete(deleteTarget.id);
      setActionMsg({ type: 'success', text: `Report for "${deleteTarget.location}" deleted.` });
      setDeleteTarget(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Delete failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [deleteTarget]);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Reports</h1>
            <p className="text-xs text-foreground-500">AI Feasibility Reports{loading ? ' (loading...)' : ` — ${reports.length} of ${totalCount} shown`}</p>
          </div>
        </div>

        {/* Report Type Tabs + Date Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {reportTypes.map(rt => (
              <button key={rt.id} onClick={() => setSelectedReport(rt.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer transition-all whitespace-nowrap ${
                selectedReport === rt.id ? 'bg-accent-100 text-accent-700 font-semibold border border-accent-200' : 'text-foreground-500 border border-background-200/60 hover:text-foreground-950'
              }`}>
                <i className={rt.icon} /> {rt.label}
              </button>
            ))}
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <i className="ri-calendar-line absolute left-3 top-1/2 -translate-y-1/2 text-background-400 text-sm pointer-events-none" />
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="pl-9 pr-3 py-2 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer "
                title="From date"
              />
            </div>
            <span className="text-foreground-500 text-xs">to</span>
            <div className="relative">
              <i className="ri-calendar-line absolute left-3 top-1/2 -translate-y-1/2 text-background-400 text-sm pointer-events-none" />
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="pl-9 pr-3 py-2 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer "
                title="To date"
              />
            </div>
            {hasDateFilter && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs text-foreground-500 hover:text-rose-400 cursor-pointer whitespace-nowrap border border-background-200/60 hover:border-rose-500/30"
              >
                <i className="ri-close-line" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Active filter indicator */}
        {hasDateFilter && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-accent-700 bg-accent-500/10 border border-accent-200 px-3 py-1 rounded-full">
              <i className="ri-filter-line text-[10px]" />
              {dateFrom && dateTo ? `${formatDate(dateFrom)} – ${formatDate(dateTo)}` : dateFrom ? `From ${formatDate(dateFrom)}` : `Until ${formatDate(dateTo)}`}
            </span>
            <span className="text-[11px] text-foreground-500">{reports.length} matching report{reports.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {stats.map(s => (
            <div key={s.label} className="rounded-xl border bg-white border-background-200/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  s.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                  s.color === 'teal' ? 'bg-teal-500/15 text-teal-400' : 'bg-secondary-500/15 text-secondary-400'
                }`}><i className={`${s.icon} text-xs`} /></span>
                <span className="text-[10px] text-foreground-500 uppercase tracking-wider">{s.label}</span>
              </div>
              <p className="font-heading text-lg font-bold text-foreground-950">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Reports Table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          {loading ? (
            <div className="p-12 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-12 bg-background-200/60 rounded-lg animate-pulse" />)}
            </div>
          ) : reports.length === 0 ? (
            <div className="p-16 text-center">
              <i className="ri-file-chart-line text-5xl text-background-400 mb-4 block" />
              <p className="text-lg font-semibold text-foreground-950 mb-1">No reports found</p>
              <p className="text-sm text-foreground-500">{hasDateFilter ? 'Try adjusting your date range or clearing filters.' : 'AI feasibility reports will appear here when users submit analysis requests.'}</p>
              {hasDateFilter && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="mt-3 px-4 py-2 rounded-lg border border-accent-200 text-sm text-accent-700 hover:bg-accent-500/10 cursor-pointer whitespace-nowrap">
                  Clear Date Filter
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Location</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Size</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Requester</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Score</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Recommended Use</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Date</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.id} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground-950 max-w-[160px] truncate">{r.location}</td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{r.land_size_acres} Acres</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground-950">{r.requester_name || '-'}</p>
                        <p className="text-[11px] text-foreground-500">{r.requester_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {r.monetization_score ? (
                          <span className={`text-sm font-bold ${r.monetization_score >= 70 ? 'text-teal-400' : r.monetization_score >= 40 ? 'text-accent-700' : 'text-rose-400'}`}>{r.monetization_score}</span>
                        ) : <span className="text-xs text-foreground-500">-</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-500 max-w-[160px] truncate">{r.recommended_use || '-'}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusBadge(r.status)}`}>{r.status}</span></td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(r.completed_at || r.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDeleteTarget(r)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-rose-400 cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                <i className="ri-delete-bin-line text-rose-400 text-xl" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Delete Report?</h3>
              <p className="text-sm text-foreground-500 mt-1">This will permanently delete the report for <strong className="text-foreground-950">{deleteTarget.location}</strong>. This cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}